import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import GeoLocationInput from "@/components/GeoLocationInput";
import FileUploadZone from "@/components/FileUploadZone";
import DichiarazioniBlock from "@/components/DichiarazioniBlock";
import ClaimsInfoSheet, { emptyClaim } from "@/components/ClaimsInfoSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { enrichLabelWithYear } from "@/lib/exerciseYear";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Building2, Plus, Trash2, Search, Download } from "lucide-react";
import { StepProgress, FormNavigation } from "@/components/StepProgress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  "Dati Anagrafici",
  "Assicurazioni Precedenti",
  "Informazioni Finanziarie",
  "Informazioni Generali",
  "Specializzazioni",
  "Attività Mediche",
  "Estensioni di Copertura",
  "Sinistrosità",
  "Dichiarazioni",
];

const TIPOLOGIE = [
  "Poliambulatorio",
  "Farmacia Privata",
  "Laboratorio Analisi",
  "Studio Odontoiatrico",
  "RSA",
  "Clinica Privata",
  "Casa di Cura",
  "Altro",
];

const ATTIVITA_SVOLTE = [
  { value: "ambulatoriale", label: "Struttura ambulatoriale che non esegue prestazioni erogabili solo in ambulatori protetti (ai sensi del DPCM 12/01/2017), ivi compresi i laboratori di analisi" },
  { value: "no_chirurgica", label: "Struttura che non svolge attività chirurgica, ortopedica, anestesiologica e parto, ivi comprese le strutture socio-sanitarie e gli ambulatori protetti" },
  { value: "chirurgica", label: "Struttura che svolge anche attività chirurgica, ortopedica, anestesiologica e parto" },
];

const MASSIMALI = [
  { value: "1m_3m", label: "€ 1.000.000 / € 3.000.000" },
  { value: "2m_6m", label: "€ 2.000.000 / € 6.000.000" },
  { value: "5m_15m", label: "€ 5.000.000 / € 15.000.000" },
];

const FRANCHIGIE = [
  { value: "1500", label: "€ 1.500" },
  { value: "2500", label: "€ 2.500" },
  { value: "5000", label: "€ 5.000" },
  { value: "7500", label: "€ 7.500" },
  { value: "10000", label: "€ 10.000" },
  { value: "15000", label: "€ 15.000" },
  { value: "20000", label: "€ 20.000" },
];

const SPECIALIZZAZIONI = [
  "Allergologia e Immunologia clinica","Anatomia patologica","Andrologia","Andrologia Invasiva",
  "Anestesia, rianimazione e terapia intensiva","Angiologia e Flebologia","Angiologia e Flebologia Invasiva",
  "Audiologia","Biochimica e chimica clinica","Biologia","Cardiochirurgia","Cardiologia","Cardiologia Invasiva",
  "Chirurgia addominale","Chirurgia d'urgenza e pronto soccorso","Chirurgia della mano","Chirurgia Generale",
  "Chirurgia Maxillo facciale con estetica","Chirurgia Maxillo Facciale senza estetica","Chirurgia pediatrica",
  "Chirurgia Plastica e Ricostruttiva con estetica","Chirurgia Ricostruttiva","Chirurgia toracica",
  "Chirurgia vascolare","Dermatologia-Venereologia","Diabetologia e malattie del metabolismo",
  "Ematologia e Medicina Trasfusionale","Endocrinochirurgia","Endocrinologia","Epatologia","Farmacologia",
  "Fisica Medica","Gastroenterologia","Gastroenterologia Invasiva","Genetica Medica","Geriatria",
  "Ginecologia con Fecondazione assistita (no Ostetricia)","Ginecologia e Ostetricia Ambulatoriale",
  "Ginecologia e Ostetricia (con Fecondazione assistita)","Ginecologia (no Ostetricia)",
  "Igiene e medicina preventiva","Immunologia","Malattie apparato cardiovascolare",
  "Malattie apparato digerente","Malattie apparato respiratorio","Malattie infettive",
  "Medicina del lavoro","Medicina di base","Medicina di comunità","Medicina d'urgenza","Medicina Estetica",
  "Medicina fisica e riabilitazione","Medicina generale","Medicina interna","Medicina legale",
  "Medicina Nucleare","Medicina Termale","Medicina tropicale","Medicine non convenzionali e osteopatiche",
  "Medico abilitato non specializzato","Microbiologia/Virologia","Nefrologia","Nefrologia Invasiva",
  "Neurochirurgia","Neurofisiopatologia","Neurologia","Neuropsichiatria","Oculistica/Oftalmologia",
  "Oculistica/Oftalmologia con estetica","Oculistica/Oftalmologia Invasiva","Odontoiatria",
  "Oncologia e senologia","Ortopedia","Ortopedia e Traumatologia (no spinali)",
  "Ortopedia e Traumatologia (con spinali)","Otorinolaringoiatria",
  "Otorinolaringoiatria senza estetica","Otorinolaringoiatria con estetica",
  "Patologia Clinica","Pediatria","Pediatria senza neonatologia","Pneumologia","Psichiatria",
  "Psicologia Clinica","Radiodiagnostica (no mammografia)","Radiodiagnostica con mammografia",
  "Radioterapia","Reumatologia","Scienze dell'alimentazione","Statistica Sanitaria e Biometria",
  "Terapia del dolore e cure palliative","Terapia del dolore Invasiva","Urologia","Urologia Invasiva",
];

const PERSONALE_NON_MEDICO = [
  "Agopuntore","Aromaterapeuta","Assistente Sociale","Biologo","Bionaturopata",
  "Career Coach","Chinesiterapeuta","Chiropratico","Consulente","Cosmetologo","Dietista",
  "Educatore Professionale","Elettro agopuntore","Erborista","Fisioterapista","Igienista Dentale",
  "Infermiere Professionale NON Ostetrico","Life Coach","Logopedista","Maestro Yoga",
  "Massaggiatore","Massaggiatore Aromatouch","Musicoterapeuta","Naturopata","Nutripuntore",
  "Odontotecnico","Operatore Shiatzu","Operatore Socio Sanitario","Ortottista","Osteopata",
  "Pedagogo","Perfusionista","Podologo","Pranoterapeuta","Psicologo","Psicologo del Lavoro",
  "Psicomotricista","Riflessologo","Sessuologo","Tatuatore","Tecnico Ai chi","Tecnico Audiologo",
  "Tecnico Audiometrista","Tecnico Camera Iperbarica","Tecnico della Riabilitazione Psichiatrica",
  "Tecnico di Alimentazione Dietetica","Tecnico di Laboratorio","Tecnico Neurofisiopatologo",
  "Tecnico Ortopedico","Tecnico per l'Ambiente del lavoro","Tecnico Radiologo","Terapeuta",
  "Terapista Audiometrico","Terapista Neurologico e Psicomotorio","Terapista Occupazionale",
  "Tossicologo Ambientale","Altro Personale",
];

const CATEGORIE_ESTETICA = [
  { id: "cat1", label: "Categoria 1", desc: "Filler, Botox, Peeling chimico, Mesoterapia, Microdermoabrasione, Laser non invasivi, Radiofrequenza, Carbossiterapia, Scleroterapia, Criolipolisi, PRP, Acido ialuronico, HIFU, Needling..." },
  { id: "cat2", label: "Categoria 2", desc: "Cat. 1 + Laser endovenosi, Rimozione tatuaggi laser, Fleboterapia rigenerativa TRAP, Laser epilazione/resurfacing/Q-Switch, IPL, Laser vascolari..." },
  { id: "cat3", label: "Categoria 3", desc: "Cat. 1+2 + Laserterapia vaginale, Radiofrequenza vaginale, Terapie ormonali, Lifting non chirurgico con fili riassorbibili..." },
  { id: "cat4", label: "Categoria 4", desc: "Cat. 1+2+3 + O-shot/P-shot, Incontinenza urinaria, ThermiVa, Liposuzione non chirurgica in anestesia locale, Fili di trazione e Milifting..." },
  { id: "cat5", label: "Categoria 5", desc: "Cat. 1+2+3+4 + Trapianto di capelli" },
];

const CIS_STRUTTURA_CONFIG = {
  professioni: [
    "Struttura Ambulatoriale","Poliambulatorio","Farmacia Privata","Laboratorio Analisi",
    "Studio Odontoiatrico","RSA","Clinica Privata","Casa di Cura","Altro (specificare)",
  ],
  categorieDanno: [
    { id: "errore_diagnostico", label: "Errore diagnostico" },
    { id: "errore_terapeutico", label: "Errore terapeutico" },
    { id: "errore_chirurgico", label: "Errore chirurgico" },
    { id: "ritardo_diagnosi", label: "Ritardo nella diagnosi / trattamento" },
    { id: "infezione_nosocomiale", label: "Infezione nosocomiale" },
    { id: "danni_fisici_paziente", label: "Danni fisici subiti dal paziente" },
    { id: "decesso_paziente", label: "Decesso del paziente" },
    { id: "consenso_informato", label: "Danni da consenso informato inadeguato" },
    { id: "mancato_rispetto_norme", label: "Mancato rispetto norme e regolamenti" },
    { id: "altro", label: "Altro (specificare)" },
  ],
  incarichi: [],
};

export default function StrutturaSanitariaPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({ sedi: [{ indirizzo: "", data_costituzione: "" }], specializzazioni_selezionate: [] });
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [specSearch, setSpecSearch] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));
  const handleFileUpload = (k, d) => { setFiles(prev => { const n = { ...prev }; if (d) n[k] = d; else delete n[k]; return n; }); };

  const totalSteps = STEPS.length;
  const progress = ((step + 1) / totalSteps) * 100;

  const showClaimsSheet = formData.sinistri_5 === "si" || formData.fatti_risc === "si";
  useEffect(() => {
    if (showClaimsSheet && (!formData.sinistri_claims || formData.sinistri_claims.length === 0)) {
      setFormData(prev => ({ ...prev, sinistri_claims: [emptyClaim()] }));
    }
  }, [showClaimsSheet, formData.sinistri_claims]);

  const handleSubmit = async () => {
    if (!formData.ragione_sociale || !formData.email || !formData.telefono) {
      toast.error("Compila tutti i campi obbligatori"); return;
    }
    if (!formData.privacy) { toast.error("Devi accettare la privacy policy"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        type: "struttura_sanitaria",
        data: formData,
        files: Object.values(files),
        contact: { nome: formData.legale_rappresentante || "", cognome: "", email: formData.email, telefono: formData.telefono }
      });
      navigate("/conferma");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nell'invio."); }
    finally { setSubmitting(false); }
  };

  const renderStep = () => {
    switch (step) {
      case 0: return <StepAnagrafica f={formData} u={update} />;
      case 1: return <StepAssicurazPrec f={formData} u={update} />;
      case 2: return <StepFinanziarie f={formData} u={update} />;
      case 3: return <StepInfoGenerali f={formData} u={update} />;
      case 4: return <StepSpecializzazioni f={formData} u={update} specSearch={specSearch} setSpecSearch={setSpecSearch} />;
      case 5: return <StepAttivitaMediche f={formData} u={update} />;
      case 6: return <StepEstensioni f={formData} u={update} />;
      case 7: return renderSinistri();
      case 8: return <StepDichiarazioni f={formData} u={update} />;
      default: return null;
    }
  };

  const renderSinistri = () => (
    <div className="space-y-5">
      <YNField label="Sono state avanzate richieste di risarcimento o azioni per accertare responsabilità civile, penale o deontologica nei confronti del Proponente negli ultimi 5 anni?" field="sinistri_5" f={formData} u={update} tid="str-sinistri" />
      <YNField label="Il Proponente è a conoscenza di azioni, omissioni o fatti dai quali possano derivare richieste di risarcimento?" field="fatti_risc" f={formData} u={update} tid="str-fatti" />
      {showClaimsSheet && formData.sinistri_claims && formData.sinistri_claims.length > 0 && (
        <div className="mt-4 p-4 sm:p-5 bg-amber-50/50 border border-amber-200/60 rounded-2xl space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            In caso di risposta affermativa, compilare il <strong>Modulo CIS</strong> sottostante.
          </div>
          <ClaimsInfoSheet claims={formData.sinistri_claims} onClaimsChange={(u) => update("sinistri_claims", u)} config={CIS_STRUTTURA_CONFIG} />
        </div>
      )}
      <FileUploadZone label="Upload documenti aggiuntivi" hint="Carica eventuali documenti a supporto" required={false} onFileUploaded={d => handleFileUpload("cis", d)} />
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Note aggiuntive</Label>
        <Textarea value={formData.det_sinistri || ""} onChange={e => update("det_sinistri", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[80px]" placeholder="Eventuali dettagli..." data-testid="str-det-sin" />
      </div>
    </div>
  );

  return (
    <><Header />
    <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="struttura-page">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-12">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
            <Building2 className="w-7 h-7 text-[#163321]" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2" data-testid="struttura-title">
            Cliniche Scudo Professionale
          </h1>
          <p className="text-sm text-[#4A5E51]">Polizza RC per Strutture Sanitarie</p>
        </div>
        <StepProgress steps={STEPS} currentStep={step} onStepClick={setStep} progress={progress} testIdPrefix="str" />
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 mb-6 shadow-sm" data-testid={`str-step-${step}`}>
          <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-5">{STEPS[step]}</h2>
          {renderStep()}
        </div>
        <FormNavigation step={step} totalSteps={totalSteps}
          onPrev={() => step === 0 ? navigate(-1) : setStep(s => s - 1)}
          onNext={() => setStep(s => s + 1)}
          onSubmit={handleSubmit} submitting={submitting} canSubmit={!!formData.privacy}
          prevLabel={step === 0 ? "Indietro" : "Indietro"} testIdPrefix="str" />
      </div>
      <PrivacyLinks className="mt-6 mb-4" />
    </main><Footer /></>
  );
}

/* ========== STEP COMPONENTS ========== */

function FField({ label, required, type = "text", value, onChange, placeholder, tid }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm text-[#0F1F14]">{label} {required && <span className="text-[#D97B58]">*</span>}</Label>
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="rounded-xl border-[#E5E2DB] h-11" placeholder={placeholder} data-testid={tid} />
    </div>
  );
}

function YNField({ label, field, f, u, tid }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm text-[#0F1F14]">{label}</Label>
      <RadioGroup value={f[field] || ""} onValueChange={v => u(field, v)} className="flex gap-4">
        <div className="flex items-center gap-2"><RadioGroupItem value="si" id={`${field}-si`} data-testid={`${tid}-si`} /><Label htmlFor={`${field}-si`} className="text-sm cursor-pointer">Sì</Label></div>
        <div className="flex items-center gap-2"><RadioGroupItem value="no" id={`${field}-no`} data-testid={`${tid}-no`} /><Label htmlFor={`${field}-no`} className="text-sm cursor-pointer">No</Label></div>
      </RadioGroup>
    </div>
  );
}

/* --- STEP 1: DATI ANAGRAFICI --- */
function StepAnagrafica({ f, u }) {
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">1.A Attività svolta <span className="text-[#D97B58]">*</span></Label>
        <RadioGroup value={f.attivita_svolta || ""} onValueChange={v => u("attivita_svolta", v)} className="space-y-3">
          {ATTIVITA_SVOLTE.map(a => (
            <div key={a.value} className="flex items-start gap-2.5">
              <RadioGroupItem value={a.value} id={`att-${a.value}`} className="mt-1 shrink-0" data-testid={`str-att-${a.value}`} />
              <Label htmlFor={`att-${a.value}`} className="text-xs text-[#0F1F14] cursor-pointer leading-snug">{a.label}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">1.B Tipologia di struttura <span className="text-[#D97B58]">*</span></Label>
        <Select value={f.tipologia || ""} onValueChange={v => u("tipologia", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="str-tipologia"><SelectValue placeholder="Seleziona tipologia" /></SelectTrigger>
          <SelectContent>{TIPOLOGIE.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <FField label="1.C Ragione Sociale" required value={f.ragione_sociale} onChange={v => u("ragione_sociale", v)} tid="str-rag-soc" />
      <GeoLocationInput label="1.G Indirizzo" required value={f.indirizzo} onChange={v => u("indirizzo", v)} testId="str-indirizzo" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FField label="1.D Città" required value={f.citta} onChange={v => u("citta", v)} tid="str-citta" />
        <FField label="1.E Provincia" value={f.provincia} onChange={v => u("provincia", v)} tid="str-prov" />
        <FField label="1.F CAP" value={f.cap} onChange={v => u("cap", v)} tid="str-cap" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FField label="1.H Codice Fiscale" required value={f.codice_fiscale} onChange={v => u("codice_fiscale", v.toUpperCase())} tid="str-cf" />
        <FField label="1.I Partita IVA" required value={f.partita_iva} onChange={v => u("partita_iva", v)} tid="str-piva" />
      </div>
      <FField label="1.N Nome e Cognome del Legale Rappresentante" required value={f.legale_rappresentante} onChange={v => u("legale_rappresentante", v)} tid="str-lr" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FField label="1.J Telefono cellulare" required type="tel" value={f.telefono} onChange={v => u("telefono", v)} placeholder="+39 333 1234567" tid="str-tel" />
        <FField label="1.K Email" required type="email" value={f.email} onChange={v => u("email", v)} placeholder="email@struttura.it" tid="str-email" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FField label="1.L Sito Web" value={f.sito_web} onChange={v => u("sito_web", v)} placeholder="www.struttura.it" tid="str-sito" />
        <FField label="1.M Anno di costituzione" value={f.anno_costituzione} onChange={v => u("anno_costituzione", v)} tid="str-anno" />
      </div>
    </div>
  );
}

/* --- STEP 2: ASSICURAZIONI PRECEDENTI --- */
function StepAssicurazPrec({ f, u }) {
  return (
    <div className="space-y-5">
      <YNField label="2.A Il Proponente è stato assicurato per questa tipologia di rischio negli ultimi 5 anni?" field="gia_assicurato" f={f} u={u} tid="str-gia-ass" />
      {f.gia_assicurato === "si" && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <FField label="2.B Assicuratori" required value={f.assicuratori_prec} onChange={v => u("assicuratori_prec", v)} tid="str-assic" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FField label="2.C Massimale" value={f.massimale_prec} onChange={v => u("massimale_prec", v)} tid="str-mass-prec" />
            <FField label="2.D Franchigia per sinistro" value={f.franchigia_prec} onChange={v => u("franchigia_prec", v)} tid="str-franch-prec" />
          </div>
          <FField label="2.E Data scadenza ultima copertura" type="date" value={f.data_scadenza} onChange={v => u("data_scadenza", v)} tid="str-data-scad" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FField label="2.F Franchigia in aggregato" value={f.franchigia_agg} onChange={v => u("franchigia_agg", v)} tid="str-franch-agg" />
            <FField label="2.G Anni copertura continua" type="number" value={f.anni_copertura} onChange={v => u("anni_copertura", v)} tid="str-anni-cop" />
          </div>
        </div>
      )}
    </div>
  );
}

/* --- STEP 3: INFORMAZIONI FINANZIARIE --- */
function StepFinanziarie({ f, u }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Indicare fatturato e retribuzioni per gli ultimi esercizi finanziari.</p>
      <div className="space-y-3 p-4 bg-[#F5F4F0] rounded-xl">
        <p className="text-xs font-bold uppercase tracking-wider text-[#4A5E51]">Fatturato Totale</p>
        <FField label={enrichLabelWithYear("Ultimo esercizio (€)")} required type="number" value={f.fatt_ultimo} onChange={v => u("fatt_ultimo", v)} tid="str-fatt-ult" />
        <FField label={enrichLabelWithYear("Penultimo esercizio (€)")} type="number" value={f.fatt_penultimo} onChange={v => u("fatt_penultimo", v)} tid="str-fatt-pen" />
        <FField label="Stima esercizio corrente (€)" type="number" value={f.fatt_stima} onChange={v => u("fatt_stima", v)} tid="str-fatt-stima" />
      </div>
      <div className="space-y-3 p-4 bg-[#F5F4F0] rounded-xl">
        <p className="text-xs font-bold uppercase tracking-wider text-[#4A5E51]">Retribuzioni Totali</p>
        <FField label={enrichLabelWithYear("Ultimo esercizio (€)")} type="number" value={f.retr_ultimo} onChange={v => u("retr_ultimo", v)} tid="str-retr-ult" />
        <FField label={enrichLabelWithYear("Penultimo esercizio (€)")} type="number" value={f.retr_penultimo} onChange={v => u("retr_penultimo", v)} tid="str-retr-pen" />
        <FField label="Stima esercizio corrente (€)" type="number" value={f.retr_stima} onChange={v => u("retr_stima", v)} tid="str-retr-stima" />
      </div>
      <div className="space-y-3 p-4 bg-[#F5F4F0] rounded-xl">
        <p className="text-xs font-bold uppercase tracking-wider text-[#4A5E51]">Fonti di reddito (%)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FField label="Pubblico / S.S.N. (%)" type="number" value={f.perc_ssn} onChange={v => u("perc_ssn", v)} tid="str-perc-ssn" />
          <FField label="Pazienti Paganti (%)" type="number" value={f.perc_paganti} onChange={v => u("perc_paganti", v)} tid="str-perc-pag" />
          <FField label="Donazioni (%)" type="number" value={f.perc_donazioni} onChange={v => u("perc_donazioni", v)} tid="str-perc-don" />
          <FField label="Liberi Prof. appoggiati (%)" type="number" value={f.perc_liberi_prof} onChange={v => u("perc_liberi_prof", v)} tid="str-perc-lp" />
        </div>
      </div>
    </div>
  );
}

/* --- STEP 4: INFORMAZIONI GENERALI --- */
function StepInfoGenerali({ f, u }) {
  const sedi = f.sedi || [{ indirizzo: "", data_costituzione: "" }];
  const updateSede = (idx, key, val) => {
    const ns = sedi.map((s, i) => i === idx ? { ...s, [key]: val } : s);
    u("sedi", ns);
  };
  const addSede = () => u("sedi", [...sedi, { indirizzo: "", data_costituzione: "" }]);
  const removeSede = (idx) => u("sedi", sedi.filter((_, i) => i !== idx));

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Label className="text-sm text-[#0F1F14]">4.A Sedi operative</Label>
        {sedi.map((s, i) => (
          <div key={i} className="flex gap-2 items-end">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={s.indirizzo} onChange={e => updateSede(i, "indirizzo", e.target.value)} placeholder={`Indirizzo sede ${i + 1}`} className="rounded-xl border-[#E5E2DB] h-11 text-sm" data-testid={`str-sede-${i}-ind`} />
              <Input type="date" value={s.data_costituzione} onChange={e => updateSede(i, "data_costituzione", e.target.value)} className="rounded-xl border-[#E5E2DB] h-11 text-sm" data-testid={`str-sede-${i}-data`} />
            </div>
            {sedi.length > 1 && (
              <button type="button" onClick={() => removeSede(i)} className="p-2 text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
        {sedi.length < 5 && (
          <Button type="button" variant="outline" onClick={addSede} className="w-full rounded-xl border-dashed border-[#163321]/30 text-[#163321] h-9 text-xs" data-testid="str-add-sede">
            <Plus className="w-3 h-3 mr-1" /> Aggiungi sede
          </Button>
        )}
      </div>
      <YNField label="4.B Il proponente svolge attività/procedure che richiedono l'anestesia generale?" field="anestesia_gen" f={f} u={u} tid="str-anest-gen" />
      {f.anestesia_gen === "si" && <Textarea value={f.anestesia_gen_det || ""} onChange={e => u("anestesia_gen_det", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" placeholder="Dettagli anestesia generale..." data-testid="str-anest-gen-det" />}
      <YNField label="4.C Il proponente svolge attività/procedure che richiedono l'anestesia locale?" field="anestesia_loc" f={f} u={u} tid="str-anest-loc" />
      {f.anestesia_loc === "si" && <Textarea value={f.anestesia_loc_det || ""} onChange={e => u("anestesia_loc_det", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" placeholder="Dettagli anestesia locale..." data-testid="str-anest-loc-det" />}
      <YNField label="4.D Vengono svolte analisi genetiche e/o prenatali?" field="analisi_genetiche" f={f} u={u} tid="str-genetiche" />
    </div>
  );
}

/* --- STEP 5: SPECIALIZZAZIONI (Sezione A) --- */
function StepSpecializzazioni({ f, u, specSearch, setSpecSearch }) {
  const selected = f.specializzazioni_selezionate || [];
  const selectedPnm = f.personale_non_medico_selezionato || [];
  const filtered = SPECIALIZZAZIONI.filter(s => s.toLowerCase().includes(specSearch.toLowerCase()));
  const toggle = (spec) => {
    const next = selected.includes(spec) ? selected.filter(x => x !== spec) : [...selected, spec];
    u("specializzazioni_selezionate", next);
  };
  const togglePnm = (att) => {
    const next = selectedPnm.includes(att) ? selectedPnm.filter(x => x !== att) : [...selectedPnm, att];
    u("personale_non_medico_selezionato", next);
  };
  const [pnmSearch, setPnmSearch] = useState("");
  const filteredPnm = PERSONALE_NON_MEDICO.filter(a => a.toLowerCase().includes(pnmSearch.toLowerCase()));

  return (
    <div className="space-y-6">
      {/* --- TABELLA 1: SPECIALIZZAZIONI MEDICHE --- */}
      <div className="space-y-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#163321]">Sezione A — Specializzazioni Mediche</p>
        <p className="text-sm text-[#4A5E51]">Seleziona le specializzazioni esercitate. Per ciascuna indica medici, procedure e visite.</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5E51]" />
          <Input value={specSearch} onChange={e => setSpecSearch(e.target.value)} placeholder="Cerca specializzazione..." className="rounded-xl border-[#E5E2DB] h-11 pl-10" data-testid="str-spec-search" />
        </div>
        <p className="text-xs text-[#4A5E51]">{selected.length} specializzazioni selezionate</p>
        <div className="max-h-[300px] overflow-y-auto space-y-1.5 border border-[#E5E2DB] rounded-xl p-3">
          {filtered.map(spec => (
            <div key={spec} className="flex items-start gap-2.5">
              <Checkbox id={`spec-${spec}`} checked={selected.includes(spec)} onCheckedChange={() => toggle(spec)} className="mt-0.5 data-[state=checked]:bg-[#163321] shrink-0" data-testid={`str-spec-${spec.substring(0, 15).replace(/\s/g, '-')}`} />
              <label htmlFor={`spec-${spec}`} className="text-xs text-[#0F1F14] cursor-pointer leading-snug">{spec}</label>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-xs text-[#4A5E51] text-center py-4">Nessuna specializzazione trovata</p>}
        </div>
        {selected.length > 0 && (
          <div className="space-y-3 mt-3">
            <p className="text-xs font-medium text-[#4A5E51]">Dettagli per specializzazione</p>
            {selected.map(spec => {
              const k = spec.replace(/[^a-zA-Z0-9]/g, '_');
              return (
                <div key={spec} className="p-3 bg-[#F5F4F0] rounded-xl space-y-2">
                  <p className="text-xs font-medium text-[#0F1F14]">{spec}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <Input type="number" placeholder="Medici dip." value={f[`s_${k}_md`] || ""} onChange={e => u(`s_${k}_md`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                    <Input type="number" placeholder="Medici lib. prof." value={f[`s_${k}_mlp`] || ""} onChange={e => u(`s_${k}_mlp`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                    <Input type="number" placeholder="Proc. chirurgiche" value={f[`s_${k}_pc`] || ""} onChange={e => u(`s_${k}_pc`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                    <Input type="number" placeholder="Proc. med. estetica" value={f[`s_${k}_pe`] || ""} onChange={e => u(`s_${k}_pe`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                    <Select value={f[`s_${k}_ce`] || ""} onValueChange={v => u(`s_${k}_ce`, v)}>
                      <SelectTrigger className="rounded-lg border-[#E5E2DB] h-9 text-xs"><SelectValue placeholder="Cat. estetica" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nessuna">Nessuna</SelectItem>
                        <SelectItem value="cat1">Cat. 1</SelectItem>
                        <SelectItem value="cat2">Cat. 2</SelectItem>
                        <SelectItem value="cat3">Cat. 3</SelectItem>
                        <SelectItem value="cat4">Cat. 4</SelectItem>
                        <SelectItem value="cat5">Cat. 5</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" placeholder="Visite special." value={f[`s_${k}_vs`] || ""} onChange={e => u(`s_${k}_vs`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* --- TABELLA 2: PERSONALE NON MEDICO --- */}
      <div className="space-y-4 pt-4 border-t border-[#E5E2DB]">
        <p className="text-xs font-bold uppercase tracking-wider text-[#163321]">Sezione A — Personale Non Medico</p>
        <p className="text-sm text-[#4A5E51]">Seleziona le attività del personale non medico presente nella struttura.</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5E51]" />
          <Input value={pnmSearch} onChange={e => setPnmSearch(e.target.value)} placeholder="Cerca attività..." className="rounded-xl border-[#E5E2DB] h-11 pl-10" data-testid="str-pnm-search" />
        </div>
        <p className="text-xs text-[#4A5E51]">{selectedPnm.length} attività selezionate</p>
        <div className="max-h-[250px] overflow-y-auto space-y-1.5 border border-[#E5E2DB] rounded-xl p-3">
          {filteredPnm.map(att => (
            <div key={att} className="flex items-start gap-2.5">
              <Checkbox id={`pnm-${att}`} checked={selectedPnm.includes(att)} onCheckedChange={() => togglePnm(att)} className="mt-0.5 data-[state=checked]:bg-[#163321] shrink-0" data-testid={`str-pnm-${att.substring(0, 15).replace(/\s/g, '-')}`} />
              <label htmlFor={`pnm-${att}`} className="text-xs text-[#0F1F14] cursor-pointer leading-snug">{att}</label>
            </div>
          ))}
          {filteredPnm.length === 0 && <p className="text-xs text-[#4A5E51] text-center py-4">Nessuna attività trovata</p>}
        </div>
        {selectedPnm.length > 0 && (
          <div className="space-y-3 mt-3">
            <p className="text-xs font-medium text-[#4A5E51]">Dettagli personale</p>
            {selectedPnm.map(att => {
              const k = att.replace(/[^a-zA-Z0-9]/g, '_');
              return (
                <div key={att} className="p-3 bg-[#F5F4F0] rounded-xl space-y-2">
                  <p className="text-xs font-medium text-[#0F1F14]">{att}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" placeholder="Prof. San. Non Med. Dip." value={f[`p_${k}_sd`] || ""} onChange={e => u(`p_${k}_sd`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                    <Input type="number" placeholder="Prof. San. Non Med. L.P." value={f[`p_${k}_slp`] || ""} onChange={e => u(`p_${k}_slp`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                    <Input type="number" placeholder="Pers. Non San. Dip." value={f[`p_${k}_nd`] || ""} onChange={e => u(`p_${k}_nd`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                    <Input type="number" placeholder="Pers. Non San. L.P." value={f[`p_${k}_nlp`] || ""} onChange={e => u(`p_${k}_nlp`, e.target.value)} className="rounded-lg border-[#E5E2DB] h-9 text-xs" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* --- STEP 6: ATTIVITÀ MEDICHE --- */
function StepAttivitaMediche({ f, u }) {
  const ANNI = [
    { key: "stima", label: "Stima esercizio corrente" },
    { key: "ultimo", label: enrichLabelWithYear("Ultimo esercizio finanziario") },
    { key: "penultimo", label: enrichLabelWithYear("Penultimo esercizio finanziario") },
  ];
  return (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">5.A Descrivere le attività del Contraente (incluse quelle degli ultimi 10 anni e nuove attività previste per i prossimi 12 mesi).</p>

      {/* POSTI LETTO - 3 anni */}
      <div className="space-y-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#163321]">Posti Letto</p>
        {ANNI.map(anno => (
          <div key={anno.key} className="p-4 bg-[#F5F4F0] rounded-xl space-y-3">
            <p className="text-xs font-medium text-[#0F1F14]">{anno.label}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <FField label="N° posti letto" type="number" value={f[`pl_${anno.key}_tot`]} onChange={v => u(`pl_${anno.key}_tot`, v)} tid={`str-pl-${anno.key}-tot`} />
              <FField label="Degenza ordinaria" type="number" value={f[`pl_${anno.key}_deg`]} onChange={v => u(`pl_${anno.key}_deg`, v)} tid={`str-pl-${anno.key}-deg`} />
              <FField label="Day hospital" type="number" value={f[`pl_${anno.key}_dh`]} onChange={v => u(`pl_${anno.key}_dh`, v)} tid={`str-pl-${anno.key}-dh`} />
              <FField label="Tasso occ. (%)" type="number" value={f[`pl_${anno.key}_occ`]} onChange={v => u(`pl_${anno.key}_occ`, v)} tid={`str-pl-${anno.key}-occ`} />
            </div>
          </div>
        ))}
      </div>

      {/* RICOVERI - 3 anni */}
      <div className="space-y-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#163321]">Ricoveri</p>
        {ANNI.map(anno => (
          <div key={anno.key} className="p-4 bg-[#F5F4F0] rounded-xl space-y-3">
            <p className="text-xs font-medium text-[#0F1F14]">{anno.label}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FField label="Ricoveri ordinari (chir. e non)" type="number" value={f[`ric_${anno.key}_ord`]} onChange={v => u(`ric_${anno.key}_ord`, v)} tid={`str-ric-${anno.key}-ord`} />
              <FField label="Ricoveri chirurgici" type="number" value={f[`ric_${anno.key}_chir`]} onChange={v => u(`ric_${anno.key}_chir`, v)} tid={`str-ric-${anno.key}-chir`} />
            </div>
          </div>
        ))}
      </div>

      <FField label="5.B Numero di reparti attualmente in attività" type="number" value={f.num_reparti} onChange={v => u("num_reparti", v)} tid="str-reparti" />
      <YNField label="5.C Vi è stata la chiusura di qualche reparto negli ultimi 10 anni?" field="chiusura_reparti" f={f} u={u} tid="str-chiusura" />
      {f.chiusura_reparti === "si" && <Textarea value={f.chiusura_det || ""} onChange={e => u("chiusura_det", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" placeholder="Indicare quale/i reparto/i e la data di chiusura..." data-testid="str-chiusura-det" />}
    </div>
  );
}

/* --- STEP 7: ESTENSIONI DI COPERTURA --- */
function StepEstensioni({ f, u }) {
  return (
    <div className="space-y-5">
      <YNField label="6.A Estensione alla Direzione Sanitaria?" field="est_direzione" f={f} u={u} tid="str-est-dir" />
      <YNField label="6.B Estensione agli interventi invasivi (art. 7.1 j)?" field="est_invasivi" f={f} u={u} tid="str-est-inv" />
      {f.est_invasivi === "si" && (
        <div className="p-3 bg-[#F5F4F0] rounded-xl text-xs text-[#4A5E51] leading-relaxed">
          <strong>Interventi invasivi:</strong> penetrazione chirurgica in tessuti/cavità/organi; sutura ferite traumatiche maggiori; cateteri cardiaci, angiografia, angioplastica; manipolazione tessuti orali; iniezione sostanze estetiche; laser non cutaneo.
        </div>
      )}
      <YNField label="6.C Estensione alla Medicina estetica?" field="est_estetica" f={f} u={u} tid="str-est-est" />
      {f.est_estetica === "si" && (
        <div className="space-y-3 pl-4 border-l-2 border-[#163321]/10">
          <p className="text-xs font-bold uppercase tracking-wider text-[#4A5E51]">Sezione B — Categorie Medicina Estetica</p>
          {CATEGORIE_ESTETICA.map(cat => (
            <div key={cat.id} className="space-y-1.5">
              <div className="flex items-start gap-2.5">
                <Checkbox id={`est-${cat.id}`} checked={f[`est_${cat.id}`] || false} onCheckedChange={v => u(`est_${cat.id}`, v)} className="mt-0.5 data-[state=checked]:bg-[#163321]" data-testid={`str-${cat.id}`} />
                <label htmlFor={`est-${cat.id}`} className="cursor-pointer">
                  <span className="text-sm font-medium text-[#0F1F14]">{cat.label}</span>
                  <p className="text-[10px] text-[#4A5E51] leading-snug mt-0.5">{cat.desc}</p>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">6.D Massimale richiesto (per sinistro / aggregato annuo) <span className="text-[#D97B58]">*</span></Label>
        <Select value={f.massimale || ""} onValueChange={v => u("massimale", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="str-massimale"><SelectValue placeholder="Seleziona massimale" /></SelectTrigger>
          <SelectContent>{MASSIMALI.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">6.E Franchigia richiesta (per sinistro) <span className="text-[#D97B58]">*</span></Label>
        <Select value={f.franchigia || ""} onValueChange={v => u("franchigia", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="str-franchigia"><SelectValue placeholder="Seleziona franchigia" /></SelectTrigger>
          <SelectContent>{FRANCHIGIE.map(fr => <SelectItem key={fr.value} value={fr.value}>{fr.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">6.F Retroattività richiesta <span className="text-[#D97B58]">*</span></Label>
        <RadioGroup value={f.retroattivita || ""} onValueChange={v => u("retroattivita", v)} className="flex gap-4">
          <div className="flex items-center gap-2"><RadioGroupItem value="10_anni" id="retro-10" data-testid="str-retro-10" /><Label htmlFor="retro-10" className="text-sm cursor-pointer">10 Anni</Label></div>
          <div className="flex items-center gap-2"><RadioGroupItem value="illimitata" id="retro-ill" data-testid="str-retro-ill" /><Label htmlFor="retro-ill" className="text-sm cursor-pointer">Illimitata</Label></div>
        </RadioGroup>
      </div>
    </div>
  );
}

/* --- STEP 9: DICHIARAZIONI --- */
function StepDichiarazioni({ f, u }) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-[#4A5E51]">Il Sottoscritto/Proponente, in qualità di Legale Rappresentante, dichiara:</p>
      <DichiarazioniBlock formData={f} update={u} />
      <div className="p-4 bg-[#F5F4F0] rounded-xl space-y-4">
        <div className="flex items-start gap-3">
          <Checkbox id="set_info_str" checked={f.set_informativo || false} onCheckedChange={v => u("set_informativo", v)} className="mt-0.5 data-[state=checked]:bg-[#163321]" data-testid="str-set-info" />
          <label htmlFor="set_info_str" className="text-xs text-[#4A5E51] leading-relaxed cursor-pointer">
            <strong>Set Informativo</strong> <span className="text-[#D97B58]">*</span><br />
            Dichiaro di aver ricevuto e preso visione dei documenti del SET INFORMATIVO (Regolamento IVASS n° 41/2018).
          </label>
        </div>
        <div className="pl-8 mt-2.5 flex flex-wrap gap-2">
          <a href="https://customer-assets.emergentagent.com/job_proposta-su-misura/artifacts/tstuy4z6_Strutture%20Sanitarie%20Private%20Scudo%20RCT%20%E2%80%93%20D.05.11.A.pdf" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-[#E5E2DB] text-[10px] font-medium text-[#163321] hover:bg-[#163321]/5 hover:border-[#163321]/30 transition-all" data-testid="str-doc-rct">
            <Download className="w-3 h-3" />
            Strutture Sanitarie Private Scudo RCT
          </a>
          <a href="https://customer-assets.emergentagent.com/job_proposta-su-misura/artifacts/2t7eyng6_DIP%20DANNI%20%2B%20DIP%20AGGIUNTIVO%20DANNI.pdf" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-[#E5E2DB] text-[10px] font-medium text-[#163321] hover:bg-[#163321]/5 hover:border-[#163321]/30 transition-all" data-testid="str-doc-dip">
            <Download className="w-3 h-3" />
            DIP Danni + DIP Aggiuntivo Danni
          </a>
        </div>
      </div>
    </div>
  );
}
